import { EntityType, ModuleId } from '../../../shared/src/models/universal_entity';

// Define which submodules are available/configured for each main module? 
// For now, let's just stick to the main ModuleId list from the shared type.
// We might need a more granular structure later (e.g. Governance -> Board vs Governance -> Members)

type ModuleConfig = {
    required: boolean;
    label: string;
};

export const MODULE_MATRIX: Record<EntityType, Record<ModuleId, ModuleConfig | null>> = {
    SOLE_PROPRIETORSHIP: {
        governance: null,
        ownership: null,
        finance: { required: true, label: 'Finance' },
        hr: { required: false, label: 'HR' }, // Optional
        payroll: { required: false, label: 'Payroll' },
        tax: { required: true, label: 'Schedule C Tax' },
        investments: { required: false, label: 'Investments' },
        grants: null,
        compliance: { required: false, label: 'Compliance' }, // DBA renewals etc
        vault: { required: true, label: 'Document Vault' },
        trust_admin: null,
        estate_admin: null,
    },

    // Grouping Partnerships for simplicity, though matrix distinguishes GP vs LP
    GENERAL_PARTNERSHIP: {
        governance: { required: false, label: 'Partnership Governance' },
        ownership: { required: true, label: 'Partner Interests' },
        finance: { required: true, label: 'Finance' },
        hr: { required: false, label: 'HR' },
        payroll: { required: false, label: 'Payroll' },
        tax: { required: true, label: 'Partnership Tax (1065)' },
        investments: { required: false, label: 'Investments' },
        grants: null,
        compliance: { required: true, label: 'Compliance' },
        vault: { required: true, label: 'Vault' },
        trust_admin: null,
        estate_admin: null,
    },

    LIMITED_PARTNERSHIP: {
        governance: { required: false, label: 'LP Governance' },
        ownership: { required: true, label: 'Partner Interests' },
        finance: { required: true, label: 'Finance' },
        hr: { required: false, label: 'HR' },
        payroll: { required: false, label: 'Payroll' },
        tax: { required: true, label: 'Partnership Tax (1065)' },
        investments: { required: true, label: 'Investments' }, // Often used for investment funds
        grants: null,
        compliance: { required: true, label: 'Compliance' },
        vault: { required: true, label: 'Vault' },
        trust_admin: null,
        estate_admin: null,
    },

    LLP: {
        governance: { required: true, label: 'Governance' },
        ownership: { required: true, label: 'Partner Interests' },
        finance: { required: true, label: 'Finance' },
        hr: { required: true, label: 'HR' },
        payroll: { required: false, label: 'Payroll' },
        tax: { required: true, label: 'Tax' },
        investments: { required: false, label: 'Investments' },
        grants: null,
        compliance: { required: true, label: 'Compliance' },
        vault: { required: true, label: 'Vault' },
        trust_admin: null,
        estate_admin: null,
    },

    LLC: {
        governance: { required: true, label: 'Governance' }, // Members/Managers
        ownership: { required: true, label: 'Ownership (Cap Table)' },
        finance: { required: true, label: 'Finance' },
        hr: { required: false, label: 'HR' },
        payroll: { required: false, label: 'Payroll' }, // Depends if S-Corp election
        tax: { required: true, label: 'Tax' },
        investments: { required: true, label: 'Investments' },
        grants: null,
        compliance: { required: true, label: 'Annual Compliance' },
        vault: { required: true, label: 'Vault' },
        trust_admin: null,
        estate_admin: null,
    },

    C_CORPORATION: {
        governance: { required: true, label: 'Governance' },
        ownership: { required: true, label: 'Cap Table' },
        finance: { required: true, label: 'Finance' },
        hr: { required: true, label: 'HR' },
        payroll: { required: true, label: 'Payroll' },
        tax: { required: true, label: 'Corporate Tax' },
        investments: { required: true, label: 'Investments' },
        grants: null,
        compliance: { required: true, label: 'Corporate Compliance' },
        vault: { required: true, label: 'Vault' },
        trust_admin: null,
        estate_admin: null,
    },

    S_CORPORATION: {
        governance: { required: true, label: 'Governance' },
        ownership: { required: true, label: 'Cap Table' },
        finance: { required: true, label: 'Finance' },
        hr: { required: true, label: 'HR' },
        payroll: { required: true, label: 'Payroll' }, // Required for S-Corp owners
        tax: { required: true, label: 'S-Corp Tax' },
        investments: { required: true, label: 'Investments' },
        grants: null,
        compliance: { required: true, label: 'Compliance' },
        vault: { required: true, label: 'Vault' },
        trust_admin: null,
        estate_admin: null,
    },

    PROFESSIONAL_CORPORATION: {
        governance: { required: true, label: 'Governance' },
        ownership: { required: true, label: 'Cap Table' },
        finance: { required: true, label: 'Finance' },
        hr: { required: true, label: 'HR' },
        payroll: { required: true, label: 'Payroll' },
        tax: { required: true, label: 'Tax' },
        investments: { required: true, label: 'Investments' },
        grants: null,
        compliance: { required: true, label: 'Compliance' },
        vault: { required: true, label: 'Vault' },
        trust_admin: null,
        estate_admin: null,
    },

    BENEFIT_CORPORATION: {
        governance: { required: true, label: 'Governance & Impact' },
        ownership: { required: true, label: 'Cap Table' },
        finance: { required: true, label: 'Finance' },
        hr: { required: true, label: 'HR' },
        payroll: { required: true, label: 'Payroll' },
        tax: { required: true, label: 'Tax' },
        investments: { required: true, label: 'Investments' },
        grants: null, // B-Corps usually don't give grants like foundations but track impact
        compliance: { required: true, label: 'Benefit Reporting' },
        vault: { required: true, label: 'Vault' },
        trust_admin: null,
        estate_admin: null,
    },

    JOINT_VENTURE: {
        governance: { required: true, label: 'JV Management' },
        ownership: { required: true, label: 'Interest Tracking' },
        finance: { required: true, label: 'Project Finance' },
        hr: { required: false, label: 'HR' },
        payroll: { required: false, label: 'Payroll' },
        tax: { required: true, label: 'Partnership Tax' },
        investments: { required: false, label: 'Investments' },
        grants: null,
        compliance: { required: true, label: 'Compliance' },
        vault: { required: true, label: 'Vault' },
        trust_admin: null,
        estate_admin: null,
    },

    PUBLIC_CHARITY: {
        governance: { required: true, label: 'Board Governance' },
        ownership: null, // No owners
        finance: { required: true, label: 'Nonprofit Finance' },
        hr: { required: true, label: 'HR' },
        payroll: { required: true, label: 'Payroll' },
        tax: { required: true, label: 'Form 990' },
        investments: { required: true, label: 'Endowment Mgmt' },
        grants: { required: false, label: 'Grantmaking' }, // Optional logic for charities that grant
        compliance: { required: true, label: 'Charity Compliance' },
        vault: { required: true, label: 'Vault' },
        trust_admin: null,
        estate_admin: null,
    },

    PRIVATE_FOUNDATION: {
        governance: { required: true, label: 'Board Governance' },
        ownership: null,
        finance: { required: true, label: 'Finance' },
        hr: { required: false, label: 'HR' },
        payroll: { required: false, label: 'Payroll' },
        tax: { required: true, label: 'Form 990-PF' },
        investments: { required: true, label: 'Investments & PRIs' },
        grants: { required: true, label: 'Grantmaking' },
        compliance: { required: true, label: 'Foundation Compliance' },
        vault: { required: true, label: 'Vault' },
        trust_admin: null,
        estate_admin: null,
    },

    PRIVATE_OPERATING_FOUNDATION: {
        governance: { required: true, label: 'Board Governance' },
        ownership: null,
        finance: { required: true, label: 'Finance' },
        hr: { required: true, label: 'HR' },
        payroll: { required: true, label: 'Payroll' },
        tax: { required: true, label: 'Form 990-PF' },
        investments: { required: true, label: 'Investments' },
        grants: { required: true, label: 'Direct Charitable' }, // POFs do direct work + grants
        compliance: { required: true, label: 'Compliance' },
        vault: { required: true, label: 'Vault' },
        trust_admin: null,
        estate_admin: null,
    },

    SUPPORTING_ORGANIZATION: { // Same as public charity mostly
        governance: { required: true, label: 'Board Governance' },
        ownership: null,
        finance: { required: true, label: 'Finance' },
        hr: { required: false, label: 'HR' },
        payroll: { required: false, label: 'Payroll' },
        tax: { required: true, label: 'Form 990' },
        investments: { required: true, label: 'Investments' },
        grants: { required: true, label: 'Distributions' },
        compliance: { required: true, label: 'Compliance' },
        vault: { required: true, label: 'Vault' },
        trust_admin: null,
        estate_admin: null,
    },

    '501C4': {
        governance: { required: true, label: 'Governance' },
        ownership: null,
        finance: { required: true, label: 'Finance' },
        hr: { required: false, label: 'HR' },
        payroll: { required: false, label: 'Payroll' },
        tax: { required: true, label: 'Form 990' },
        investments: { required: false, label: 'Investments' },
        grants: null,
        compliance: { required: true, label: 'Compliance' },
        vault: { required: true, label: 'Vault' },
        trust_admin: null,
        estate_admin: null,
    },
    '501C6': {
        governance: { required: true, label: 'Governance' },
        ownership: null,
        finance: { required: true, label: 'Finance' },
        hr: { required: false, label: 'HR' },
        payroll: { required: false, label: 'Payroll' },
        tax: { required: true, label: 'Form 990' },
        investments: { required: false, label: 'Investments' },
        grants: null,
        compliance: { required: true, label: 'Compliance' },
        vault: { required: true, label: 'Vault' },
        trust_admin: null,
        estate_admin: null,
    },
    '501C7': {
        governance: { required: true, label: 'Club Governance' },
        ownership: { required: true, label: 'Membership' }, // Social clubs have members
        finance: { required: true, label: 'Finance' },
        hr: { required: false, label: 'HR' },
        payroll: { required: false, label: 'Payroll' },
        tax: { required: true, label: 'Form 990' },
        investments: { required: false, label: 'Investments' },
        grants: null,
        compliance: { required: true, label: 'Compliance' },
        vault: { required: true, label: 'Vault' },
        trust_admin: null,
        estate_admin: null,
    },
    'HOA': {
        governance: { required: true, label: 'Board' },
        ownership: { required: true, label: 'Homeowners' },
        finance: { required: true, label: 'Finance' },
        hr: { required: false, label: 'HR' },
        payroll: { required: false, label: 'Payroll' },
        tax: { required: true, label: 'Form 1120-H' },
        investments: { required: true, label: 'Reserves' },
        grants: null,
        compliance: { required: true, label: 'Compliance' },
        vault: { required: true, label: 'Vault' },
        trust_admin: null,
        estate_admin: null,
    },

    REVOCABLE_TRUST: {
        governance: null,
        ownership: { required: true, label: 'Beneficiaries' },
        finance: { required: true, label: 'Trust Accounting' },
        hr: null,
        payroll: null,
        tax: { required: true, label: 'Grantor Tax Info' },
        investments: { required: true, label: 'Assets' },
        grants: null,
        compliance: null,
        vault: { required: true, label: 'Vault' },
        trust_admin: { required: true, label: 'Trust Admin' },
        estate_admin: null,
    },

    IRREVOCABLE_TRUST: {
        governance: null, // Trustees manages, but not "governance" in corp sense
        ownership: { required: true, label: 'Beneficiaries' },
        finance: { required: true, label: 'Trust Accounting' },
        hr: null,
        payroll: null,
        tax: { required: true, label: 'Form 1041' },
        investments: { required: true, label: 'Assets' },
        grants: null,
        compliance: null,
        vault: { required: true, label: 'Vault' },
        trust_admin: { required: true, label: 'Trust Admin' },
        estate_admin: null,
    },

    CHARITABLE_TRUST: {
        governance: null,
        ownership: { required: true, label: 'Beneficiaries' },
        finance: { required: true, label: 'Trust Accounting' },
        hr: null,
        payroll: null,
        tax: { required: true, label: 'Form 5227' },
        investments: { required: true, label: 'Assets' },
        grants: { required: true, label: 'Distributions' },
        compliance: null,
        vault: { required: true, label: 'Vault' },
        trust_admin: { required: true, label: 'Trust Admin' },
        estate_admin: null,
    },

    SPECIAL_NEEDS_TRUST: {
        governance: null,
        ownership: { required: true, label: 'Beneficiary' },
        finance: { required: true, label: 'Accounting' },
        hr: null,
        payroll: null,
        tax: { required: true, label: 'Form 1041' },
        investments: { required: true, label: 'Assets' },
        grants: null,
        compliance: { required: true, label: 'SSI Compliance' }, // Specific compliance
        vault: { required: true, label: 'Vault' },
        trust_admin: { required: true, label: 'Trust Admin' },
        estate_admin: null,
    },

    ESTATE: {
        governance: null,
        ownership: null, // Heirs
        finance: { required: true, label: 'Estate Accounting' },
        hr: null,
        payroll: null,
        tax: { required: true, label: '706 / 1041' },
        investments: { required: true, label: 'Inventory' },
        grants: null,
        compliance: { required: true, label: 'Probate Deadlines' },
        vault: { required: true, label: 'Vault' },
        trust_admin: null,
        estate_admin: { required: true, label: 'Probate Admin' },
    },

    INDIVIDUAL: {
        governance: null,
        ownership: null,
        finance: null,
        hr: null,
        payroll: null,
        tax: { required: true, label: '1040 Prep' },
        investments: null,
        grants: null,
        compliance: null,
        vault: { required: true, label: 'Personal Vault' },
        trust_admin: null,
        estate_admin: { required: true, label: 'Estate Plan' }, // Using estate admin for prep? Or just vault.
    }
};

export const getModulesFor = (type: EntityType) => MODULE_MATRIX[type];
